import Tutorial from './ExerciseData';
import TimeKeeper from 'Models/TimeKeeper';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { MIDIValInput } from '@midival/core';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import { ApptourFeature, ApptourFeatureItem } from 'Utils/AppTourFeatures';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import type { FingeringDisplay } from 'Fingering/display';
import { LessonActions } from './LessonTypes';
import { RepertoireActions } from './RepertoireTypes';
import { EventActions } from './EventTypes';
import { TutorialAction } from './TutorialTypes';

export enum MidiEventType {
  NOTE_ON = 'NOTE_ON',
  NOTE_OFF = 'NOTE_OFF',
}

export enum ERROR_RECOGNITION_MODE {
  AUDIO = 'AUDIO',
  MIDI = 'MIDI',
}

export enum MidiEventState {
  HIT = 'HIT',
  UNSET = 'UNSET',
  WRONG_NOTE = 'WRONG_NOTE',
  NEVER_PLAYED = 'NEVER_PLAYED',
  EARLY_ONSET = 'EARLY_ONSET',
  LATE_ONSET = 'LATE_ONSET',
  EARLY_OFFSET = 'EARLY_OFFSET',
  LATE_OFFSET = 'LATE_OFFSET',
  ADDITIONAL_NOTE = 'ADDITIONAL_NOTE',
}

export type MidiFullErrorStates = MidiEventState.WRONG_NOTE | MidiEventState.NEVER_PLAYED;

export const midiFullErrorStates = [MidiEventState.WRONG_NOTE, MidiEventState.NEVER_PLAYED];
export type MidiSemiErrorStates =
  | MidiEventState.EARLY_ONSET
  | MidiEventState.EARLY_OFFSET
  | MidiEventState.LATE_ONSET
  | MidiEventState.LATE_OFFSET;

export const midiSemiErrorStates = [
  MidiEventState.EARLY_ONSET,
  MidiEventState.EARLY_OFFSET,
  MidiEventState.LATE_ONSET,
  MidiEventState.LATE_OFFSET,
  MidiEventState.ADDITIONAL_NOTE,
];

export type MidiAllErrorStates = MidiSemiErrorStates & MidiFullErrorStates;

export const midiNonErrorState = 'CORRECT';

export const midiAllErrorStates = midiSemiErrorStates.concat(midiFullErrorStates);

export const errorScores = {
  [MidiEventState.WRONG_NOTE]: 1,
  [MidiEventState.NEVER_PLAYED]: 1,
  [MidiEventState.EARLY_OFFSET]: 0.7,
  [MidiEventState.EARLY_ONSET]: 0.7,
  [MidiEventState.LATE_OFFSET]: 0.7,
  [MidiEventState.LATE_ONSET]: 0.7,
  [MidiEventState.ADDITIONAL_NOTE]: 0.7,
};
// This purpose of all these options is actually more backend. Originally we mapped all "semi correct" errors
// to a single "OFF_TIME" note state, which would be orange (not red or green), but it will be more useful for
// our models to have more specific error types.
export const NoteEventAccuracyMap = {
  [MidiEventState.HIT]: 1,
  [MidiEventState.NEVER_PLAYED]: 0,
  [MidiEventState.WRONG_NOTE]: 0,
  [MidiEventState.EARLY_ONSET]: 0.7,
  [MidiEventState.LATE_ONSET]: 0.7,
  [MidiEventState.EARLY_OFFSET]: 0.7,
  [MidiEventState.LATE_OFFSET]: 0.7,
  [MidiEventState.ADDITIONAL_NOTE]: 0.7,
};

// useful for using as key type
export type NoteTypeAsList =
  | NoteType._1024th
  | NoteType._512th
  | NoteType._256th
  | NoteType._128th
  | NoteType._64th
  | NoteType._32nd
  | NoteType._16th
  | NoteType.EIGTH
  | NoteType.QUARTER
  | NoteType.HALF
  | NoteType.WHOLE
  | NoteType.QUARTER
  | NoteType.BREVE
  | NoteType.UNDEFINED
  | NoteType.LONG
  | NoteType.MAXIMA;

export enum NoteColor {
  CORRECT = '#FF0000',
  INCORRECT = '#00CC00',
}

export type TickNoteEventType = CustomEvent & {
  detail: {
    name: string;
    ticks: number;
    timekeeper: TimeKeeper;
    workerState: object;
  };
};

// Way over simplistic for now. E.g. There's no way this will have "NoteType" on it.
export type MusicXMLNote = {
  time: number;
  duration: number;
  midi: number;
  note: string;
  noteType: NoteType;
};

export type MidiEvent = {
  velocity: number;
  duration: number;
  midi: number;
  ticks: number;
  name: string;
  // TODO: Change this to eventType. "event" is confusing.
  event: MidiEventType;
  time: number;
  channel: number;
};

export type AddressData = {
  city: string;
  state: string;
  zipCode: number;
  address1: string;
  address2: string;
};

export type StateData = {};

export type HistoryData = {};

export type UpdateAuthDataValues = {
  emailOrPhone: string;
  countryAcronym: string;
  name: string;
  unknown: string;
};

export type SignUpData = {
  emailOrPhone: string;
  countryAcronym: string;
  name: string;
  password: string;
  repeatPassword: string;
  unknown: string;
};

export type SignInData = {
  emailOrPhone: string;
  countryAcronym: string;
  password: string;
  unknown: string;
};

export type ResetCodeData = {
  code: string;
  unknown: string;
};

export type ChangePasswordData = {
  currentPassword: string;
  newPassword: string;
  repeatNewPassword: string;
  unknown: string;
};

export type ResetPasswordData = {
  password: string;
  repeatPassword: string;
  unknown: string;
};

export type ForgotPasswordData = {
  emailOrPhone: string;
  countryAcronym: string;
  unknown: string;
};

export interface LessonPageProps {
  className?: string;
}

export interface MenuIconProps {
  className?: string;
}

export interface CardProps {
  className?: string;
}

// Type isn't exported?
export type AmplifyAuthError = {
  code: string;
  message: string;
};

export interface AccuracyProps {
  className?: string;
  accuracy: number;
  accuracyChange: number;
}

export interface TempoProps {
  className?: string;
  tempo: number;
  tempoChange: number;
}

export interface AppDataProps {
  levelSelect: number;
}

export interface IClientData {
  userId: number;
  playMode: string;
  lastScreen: string;
  speakerMode: string;
}

export interface FetchClientDataSuccessPayload {
  data: IClientData;
}

export interface FetchClientDataErrorPayload {
  error: string;
}

export interface FetchClientDataRequestPayload {
  jwt: string;
}

export interface SetLevelSelectPayload {
  levelSelect: number;
}

export interface SetUnitSelectPayload {
  unitSelect: number;
}

export interface ITutorialData {
  template_number: number;
  title: string;
  info_text: string;
  srcs: string;
}

export interface FetchTutorialDataSuccessPayload {
  tutorialData: ITutorialData[];
}

export interface FetchTutorialDataPayload {}

export interface UserInfo {
  user: AugmentedCognitoUser | null;
}

export enum LevelStatus {
  active = 'active',
  complete = 'complete',
}

export type CognitoUserEtc = {
  attributes: Partial<UserAttributes>;
} & { username: string } & {
  signInUserSession: { accessToken: { jwtToken: string } };
};

export type AugmentedCognitoUser = CognitoUser & CognitoUserEtc;
export type InitialFederatedUserNoAttributes = {};

// "signInUserSession": {
//         "idToken": {
//             "payload": {
//                 ...
//                 "name": "Steven Staley",
//                 ...
//                 "email": "steven.staley@museflow.ai"
//             }
//         },
//         "accessToken": {
//             "payload": {
//               ...
//                 "sub": "9a75c93b-f333-4a42-bc59-0a5b0ea16357",
//         },
//     }
export type UserAttributes = {
  email: string;
  email_verified: boolean;
  name: string;
  phone_number: string;
  sub: string;
  'custom:is_test_user': boolean;
  'custom:user_type': string;
};

export type UserLevelData = {
  bpm: number; //120
  createdAt: string; //"2023-03-16T01:29:42.749Z"
  current_tier: number; //1
  current_tier_accuracy: number; //null
  deletedAt: number; //null
  highest_tier_complete: number; //0
  highest_tier_completed_at: string; //null
  id: number; //1
  level_completed_at: string; //null
  level: {
    level_number: number; //1
  };
  play_session: {
    id?: string | null;
  };
  phrases_til_next_tier: number; //4
  status: LevelStatus; //"active"
  updatedAt: string; //"2023-03-16T01:29:42.749Z"
  user_id: string; //1
};

export interface UserLevelDataSuccessPayload {
  userLevelData: UserLevelData[];
}

export type LevelData = {
  level_number: number;
  // unit_number: string;
  unit: {
    unit_number: number;
  };
  name: string;
  description: string;
  srcs: string;
};

export type PhraseData = {
  createdAt: string;
  id: number;
  s3_batch_datetimestamp: string;
  s3_music_xml_id: string;
  s3_music_xml_url: string;
  s3_svg_timesig_url: string;
  s3_svg_url: string;
  tagging_version: string;
  tier_number: number;
  updatedAt: string;
};

export type TiersByLevels = {
  level_number: number;
  tiers: number[];
};

export interface LevelDataSuccessPayload {
  levelData: LevelData[];
}

export interface UpdateCurrentULPPayload {
  currentUserLevelProgress: Partial<UserLevelData> | undefined;
}

export type UnitData = {
  unit_number: number;
  name: string;
  description: string;
};

export type TutorialData = {
  template_number: number;
  title: string;
  info_text: string;
  srcs: string;
};

export type ObjectiveData = {
  chapter: number;
  objective: number;
  phrase: {
    time_signature_numerator: number;
    time_signature_denominator: number;
    s3_music_xml_url: string;
    s3_music_xml_id: string;
    music_xml_url_pointers: {
      json_data_url_1440: string;
    };
  };
};

export type TutorialData2 = {
  level_number: number;
  objective_data: ObjectiveData[];
  tutorial_config: Tutorial;
};

export enum actionTypes {
  CLEAR_DATA = 'CLEAR_DATA',
  FETCH_CLIENT_DATA_REQUEST = 'FETCH_CLIENT_DATA_REQUEST',
  FETCH_CLIENT_DATA_SUCCESS = 'FETCH_CLIENT_DATA_SUCCESS',
  FETCH_CLIENT_DATA_ERROR = 'FETCH_CLIENT_DATA_ERROR',
  SET_PLAY_ON_START = 'SET_PLAY_ON_START',
  SET_LEVEL_SELECT = 'SET_LEVEL_SELECT',
  SET_UNIT_SELECT = 'SET_UNIT_SELECT',
  SET_TEMPO = 'SET_TEMPO',
  FETCH_TUTORIAL_DATA = 'FETCH_TUTORIAL_DATA',
  FETCH_TUTORIAL_DATA_PROCESSING = 'FETCH_TUTORIAL_PROCESSING',
  FETCH_TUTORIAL_DATA_SUCCESS = 'FETCH_TUTORIAL_DATA_SUCCESS',
  MIDI_INPUT_SETUP = 'MIDI_INPUT_SETUP',
  AUTHENTICATED_STARTUP = 'AUTHENTICATED_STARTUP',
  USER_DATA_REQUEST = 'USER_DATA_REQUEST',
  FETCH_USER_LEVEL_SUCCESS = 'FETCH_USER_LEVEL_SUCCESS',
  FETCH_LEVEL_DATA_SUCCESS = 'FETCH_LEVEL_DATA_SUCCESS',
  UPDATE_PHRASES_TIL_NEXT_TIER = 'UPDATE_PHRASES_TIL_NEXT_TIER',
  SET_LAST_SHOWN_SURVEY = 'SET_LAST_SHOWN_SURVEY',
  SET_SHOWN_INTRODUCTION = 'SET_SHOWN_INTRODUCTION',
  SET_USER_DATA = 'SET_USER_DATA',
  UPDATE_USER_DATA = 'UPDATE_USER_DATA',
  SET_AUDIO_ON = 'SET_AUDIO_ON',
  SET_COLORBLIND_TYPE = 'SET_COLORBLIND_TYPE',
  SET_REP_METRONOME_SOUND = 'SET_REP_METRONOME_SOUND',
  SET_REP_DOWNBEATS_SOUND = 'SET_REP_DOWNBEATS_SOUND',
  SET_SIGHT_READING_CURSOR_VISIBLE = 'SET_SIGHT_READING_CURSOR_VISIBLE',
  SET_SIGHT_READING_FINGERING_DISPLAY = 'SET_SIGHT_READING_FINGERING_DISPLAY',
  LOAD_USER_STATE = 'LOAD_USER_STATE',
  UPDATE_MAILCHIMP_INFO = 'UPDATE_MAILCHIMP_INFO',
  SET_FULL_SCREEN = 'SET_FULL_SCREEN',
  GET_SUBSCRIPTION_STATUS = 'GET_SUBSCRIPTION_STATUS',
  SET_SUBSCRIPTION_STATUS = 'SET_SUBSCRIPTION_STATUS',
  SET_REP_PAGE_SELECTED = 'SET_REP_PAGE_SELECTED',
  SET_ERROR_RECOGNITION_ACTIVE = 'SET_ERROR_RECOGNITION_ACTIVE',
  SET_SHOW_LEVEL_PREVIEW = 'SET_SHOW_LEVEL_PREVIEW',
  UPDATE_TIERS_BY_LEVELS = 'UPDATE_TIERS_BY_LEVELS',
  SET_INTERACTED_WITH_PAGE = 'SET_INTERACTED_WITH_PAGE',
  INVALIDATE_DASHBOARD_CACHE = 'INVALIDATE_DASHBOARD_CACHE',
  SET_DASHBOARD_DATA = 'SET_DASHBOARD_DATA',
  SET_ZOOM_LEVEL = 'SET_ZOOM_LEVEL',
  SET_AUTO_TEMPO_ADJUSTMENT = 'SET_AUTO_TEMPO_ADJUSTMENT',
  SET_CUSTOM_GOAL_TEMPO = 'SET_CUSTOM_GOAL_TEMPO',
  SET_USING_CUSTOM_GOAL_TEMPO = 'SET_USING_CUSTOM_GOAL_TEMPO',
  SET_UNSEEN_APPTOUR_FEATURES = 'SET_UNSEEN_APPTOUR_FEATURES',
  MARK_APPTOUR_FEATURES_AS_SEEN = 'MARK_APPTOUR_FEATURES_AS_SEEN',
  FETCH_UNSEEN_APPTOUR_FEATURES = 'FETCH_UNSEEN_APPTOUR_FEATURES',
  SET_CUSTOM_ATA_SETTINGS = 'SET_CUSTOM_ATA_SETTINGS',
  LOAD_ATA_SETTINGS = 'LOAD_ATA_SETTINGS',
}

export interface FetchClientDataRequest {
  type: typeof actionTypes.FETCH_CLIENT_DATA_REQUEST;
}

export type FetchClientDataSuccess = {
  type: typeof actionTypes.FETCH_CLIENT_DATA_SUCCESS;
  payload: FetchClientDataSuccessPayload;
};

export type FetchClientDataError = {
  type: typeof actionTypes.FETCH_CLIENT_DATA_ERROR;
  payload: FetchClientDataErrorPayload;
};

export type setPlayOnStart = {
  type: typeof actionTypes.SET_PLAY_ON_START;
  payload: { playOnStart: boolean };
};

export type SetLevelSelect = {
  type: typeof actionTypes.SET_LEVEL_SELECT;
  payload: SetLevelSelectPayload;
};

export type SetUnitSelect = {
  type: typeof actionTypes.SET_UNIT_SELECT;
  payload: SetUnitSelectPayload;
};

export type SetTempo = {
  type: typeof actionTypes.SET_TEMPO;
  payload: { tempo: number; level: number; tier: number };
};

export type FetchTutorialData = {
  type: typeof actionTypes.FETCH_TUTORIAL_DATA;
};

export type FetchTutorialDataProcessing = {
  type: typeof actionTypes.FETCH_TUTORIAL_DATA_PROCESSING;
  payload: boolean;
};

export type FetchTutorialDataSuccess = {
  type: typeof actionTypes.FETCH_TUTORIAL_DATA_SUCCESS;
  payload: FetchTutorialDataSuccessPayload;
};

export type MidiInputSetup = {
  type: typeof actionTypes.MIDI_INPUT_SETUP;
  payload: MIDIValInput;
};

export type SetLastShowSurvey = {
  type: typeof actionTypes.SET_LAST_SHOWN_SURVEY;
  payload: string;
};

export type SetShownIntroduction = {
  type: typeof actionTypes.SET_SHOWN_INTRODUCTION;
  payload: boolean;
};

export type SetUserData = {
  type: typeof actionTypes.SET_USER_DATA;
  payload: IUserData;
};

export type UpdateUserData = {
  type: typeof actionTypes.UPDATE_USER_DATA;
  payload: IUserData;
};

export type AuthenticatedStartup = {
  type: typeof actionTypes.AUTHENTICATED_STARTUP;
  payload: UserInfo;
};

export type UserDataRequest = {
  type: typeof actionTypes.USER_DATA_REQUEST;
  payload: UserInfo;
};

export type FetchUserLevelSuccess = {
  type: typeof actionTypes.FETCH_USER_LEVEL_SUCCESS;
  payload: UserLevelDataSuccessPayload;
};

export type FetchLevelDataSuccess = {
  type: typeof actionTypes.FETCH_LEVEL_DATA_SUCCESS;
  payload: LevelDataSuccessPayload;
};

export type UpdateTiersByLevels = {
  type: typeof actionTypes.UPDATE_TIERS_BY_LEVELS;
  payload: TiersByLevels[];
};

export type LoadUserState = {
  type: typeof actionTypes.LOAD_USER_STATE;
  payload: AppState;
};

export type setLastShowSurvey = {
  type: typeof actionTypes.SET_LAST_SHOWN_SURVEY;
};

export type setShownIntroduction = {
  type: typeof actionTypes.SET_SHOWN_INTRODUCTION;
};

export type setUserData = {
  type: typeof actionTypes.SET_USER_DATA;
  payload: IUserData;
};

export type SetAudioOn = {
  type: typeof actionTypes.SET_AUDIO_ON;
  payload: {
    audioOn: boolean;
  };
};

export type SetColorblindType = {
  type: typeof actionTypes.SET_COLORBLIND_TYPE;
  payload: COLORBLIND_TYPE;
};

export type SetRepMetronomeSound = {
  type: typeof actionTypes.SET_REP_METRONOME_SOUND;
  payload: boolean;
};

export type SetRepDownbeatsSound = {
  type: typeof actionTypes.SET_REP_DOWNBEATS_SOUND;
  payload: boolean;
};

export type SetSightReadingCursorVisible = {
  type: typeof actionTypes.SET_SIGHT_READING_CURSOR_VISIBLE;
  payload: boolean;
};

export type SetSightReadingFingeringDisplay = {
  type: typeof actionTypes.SET_SIGHT_READING_FINGERING_DISPLAY;
  payload: FingeringDisplay;
};

export type SetRepPageSelected = {
  type: typeof actionTypes.SET_REP_PAGE_SELECTED;
  payload: string;
};

export type ErrorRecognitionActive = {
  left: boolean;
  right: boolean;
};
export type SetErrorRecognitionActive = {
  type: typeof actionTypes.SET_ERROR_RECOGNITION_ACTIVE;
  payload: ErrorRecognitionActive;
};

export type SetInteractedWithPage = {
  type: typeof actionTypes.SET_INTERACTED_WITH_PAGE;
  payload: boolean;
};

export type InvalidateDashboardCache = {
  type: typeof actionTypes.INVALIDATE_DASHBOARD_CACHE;
};

export type DashboardData = {
  weeklyIntensity: Array<Array<{ date: string; value: number; minutes?: number }>>;
  totalMinutesPlayed: number;
  recentLevelAverages: Array<{
    level_number: number;
    tier_number: number;
    avg_accuracy: number;
    avg_lh_accuracy: number;
    avg_rh_accuracy: number;
  }>;
  weeklyMinutes: Array<{ week_start: string; minutes: number }>;
  totalSessionsLastYear: number;
  longestStreakDays: number;
  currentStreakDays: number;
  totalActiveDays: number;
  runningRepertoireCompleted: Array<{ week: string; cumulative_count: number }>;
  runningHighestLevel: Array<{ week: string; highest_level: number }>;
  levelsCompleted: number;
  totalLevels: number;
};

export type SetDashboardData = {
  type: typeof actionTypes.SET_DASHBOARD_DATA;
  payload: DashboardData | null;
};

export type SetShowLevelPreview = {
  type: typeof actionTypes.SET_SHOW_LEVEL_PREVIEW;
  payload: boolean;
};

export enum UserType {
  user = 'user',
}

export type AtaSettings = {
  ataEnabled: boolean;
  //goalTempo: number;
  goalTemposByTier: Record<number, number>;
  usingCustomGoalTempo: boolean;
  customIncThreshold: number;
  customDecThreshold: number;
  customIncAmt: number;
  customDecAmt: number;
  usingCustomSettings: boolean;
};

export interface AppState {
  errors: string[];
  loading: boolean;
  data: IClientData[];
  unitSelect: number;
  levelSelect: number;
  tempo: number;
  tutorialData: ITutorialData[];
  tutorialDataProcessing: boolean;
  userLevelData: UserLevelData[];
  levelData: LevelData[];
  tiersByLevels: TiersByLevels[];
  midiInput: MIDIValInput | null;
  lastShownSurvey: string | null;
  isMidiConnected: boolean | null;
  hasShownIntroduction: boolean | null;
  userData: Partial<IUserData> | null;
  userDataFetched: boolean;
  isFullScreen: boolean;
  repPageSelected: string;
  errorRecognitionActive: ErrorRecognitionActive;
  playOnStart: boolean;
  interactedWithPage: boolean;
  showLevelPreview: boolean;
  currentUserLevelData?: UserLevelData;
  currentLevelTiers?: TiersByLevels;
  pendingDeletion: boolean;
  dashboardCacheInvalidated: boolean;
  dashboardData: DashboardData | null;
  zoomLevel: number;
  tempoValues: Record<number, Record<number, number>>; // level -> tier -> tempo value
  ataSettings: AtaSettings; // ata settings are global to all tiers and levels
  unseenApptourFeatures: ApptourFeatureItem[]; // ordered by id ASC, from /user-data/get-unseen-features
  sightReadingCursorVisible: boolean; // persisted cursor toggle for sight reading; defaults to on
  sightReadingFingeringDisplay: FingeringDisplay; // persisted finger-number density for sight reading
}

export interface IUserData {
  stripe_customer_id: string;
  subscription_status: string;
  coupon_code: string | null;
  audio_on: boolean;
  prefer_audio_recognition_input?: boolean;
  practice_dark_mode?: boolean;
  practice_score_theme?: ScoreTheme;
  rep_metronome_sound: boolean;
  rep_downbeats_sound: boolean;
  // Semitone offset applied to sight-reading exercises. 0 = concert pitch.
  sight_reading_transposition: number;
  user_id?: string;
  last_shown_survey?: Date | null;
  name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  accepts_mailing_list?: boolean | null;
  game_tutorial_shown?: Date | null;
  user_type?: UserType | null;
  is_test_user?: boolean | null;
  colorblind_type: COLORBLIND_TYPE | null;
  lesson_tutorial_seenat: Date | null;
  repertoire_tutorial_seenat: Date | null;
  pending_deletion: boolean;
  subscription_provider: string | null;
  apple_receipt: string | null;
  /** When the account record was created (ISO time), as the backend returns it. */
  createdAt?: string | null;
  /** When the backend last changed `subscription_status` (ISO time). */
  subscription_status_changed_at?: string | null;
}

export type SetFullScreen = {
  type: typeof actionTypes.SET_FULL_SCREEN;
  payload: boolean;
};

export type GetSubscriptionStatus = {
  type: typeof actionTypes.GET_SUBSCRIPTION_STATUS;
  payload: boolean;
};

export type SetSubscriptionStatus = {
  type: typeof actionTypes.SET_SUBSCRIPTION_STATUS;
  payload: string;
};

export interface MainAppReducer {
  mainAppReducer: AppState;
}

export interface ClearData {
  type: typeof actionTypes.CLEAR_DATA;
}

export interface SetZoomLevel {
  type: typeof actionTypes.SET_ZOOM_LEVEL;
  payload: number;
}

export interface SetAutoTempoAdjustment {
  type: typeof actionTypes.SET_AUTO_TEMPO_ADJUSTMENT;
  payload: boolean;
}

export interface SetCustomGoalTempo {
  type: typeof actionTypes.SET_CUSTOM_GOAL_TEMPO;
  payload: { tempo: number; tier: number };
}

export interface SetUsingCustomGoalTempo {
  type: typeof actionTypes.SET_USING_CUSTOM_GOAL_TEMPO;
  payload: boolean;
}

export interface SetCustomAtaSettings {
  type: typeof actionTypes.SET_CUSTOM_ATA_SETTINGS;
  payload: {
    customIncAmt?: number;
    customDecAmt?: number;
    customIncThreshold?: number;
    customDecThreshold?: number;
    usingCustomSettings?: boolean;
  };
}

// The subset of AtaSettings persisted to the backend per user, hydrated on
// sign-in. Separate action from SetCustomAtaSettings so loading server values
// into the reducer does not itself trigger a persistence write (see AppData saga).
export interface LoadAtaSettings {
  type: typeof actionTypes.LOAD_ATA_SETTINGS;
  payload: {
    usingCustomSettings?: boolean;
    customIncThreshold?: number;
    customDecThreshold?: number;
    customIncAmt?: number;
    customDecAmt?: number;
  };
}

export type AppActions =
  | FetchClientDataRequest
  | FetchClientDataSuccess
  | FetchClientDataError
  | setPlayOnStart
  | SetLevelSelect
  | SetUnitSelect
  | SetTempo
  | FetchTutorialData
  | FetchTutorialDataProcessing
  | FetchTutorialDataSuccess
  | AuthenticatedStartup
  | UserDataRequest
  | FetchUserLevelSuccess
  | FetchLevelDataSuccess
  | UpdateTiersByLevels
  | MidiInputSetup
  | SetLastShowSurvey
  | SetShownIntroduction
  | SetUserData
  | UpdateUserData
  | SetAudioOn
  | SetColorblindType
  | SetRepMetronomeSound
  | SetRepDownbeatsSound
  | SetSightReadingCursorVisible
  | SetSightReadingFingeringDisplay
  | LoadUserState
  | SetFullScreen
  | GetSubscriptionStatus
  | SetSubscriptionStatus
  | SetRepPageSelected
  | SetErrorRecognitionActive
  | SetInteractedWithPage
  | InvalidateDashboardCache
  | SetDashboardData
  | ClearData
  | SetZoomLevel
  | SetAutoTempoAdjustment
  | SetCustomGoalTempo
  | SetUsingCustomGoalTempo
  | SetUnseenApptourFeatures
  | MarkApptourFeaturesAsSeen
  | FetchUnseenApptourFeatures
  | SetCustomAtaSettings
  | LoadAtaSettings;

export type PrerenderedGraphics = {
  staveNote: {
    id: string;
    left?: number;
  };
};

export type Pitch = {
  accidental: number;
  /** Explicit notation accidental. `null` means the key signature supplies it. */
  displayAccidental?: '#' | 'b' | 'n' | '##' | null;
  fundamentalNote: number;
  octave: number;
  halfTone: number;
  frequency: number;
};

type PlacementEnumString =
  | 'Above'
  | 'Below'
  | 'Left'
  | 'Right'
  | 'NotYetDefined'
  | 'AboveOrBelow'
  | 'Unknown';

export type Fingering = {
  placement: PlacementEnumString;
  type: string;
  value: string;
};

// Serialized tie grouping emitted by the prerenderer (musicxml-tagging
// ImageGenerator.serializeIterator). It is NOT the OSMD `Tie` object - only the
// facts the grading path needs. `isTieStart` marks the first note of the group
// (which becomes one sustained expected-note); `tieDuration` is the combined
// real-value duration of the whole group.
export type SerializedNoteTie = {
  // NotesCount is carried by the serialized-prerender (lambda) path only; the
  // live-OSMD path omits it. isTieStart + tieDuration are the fields the grading
  // path consumes and are present on both paths.
  NotesCount?: number;
  isTieStart: boolean;
  tieDuration: number;
  // Notehead highlighting (live-OSMD / repertoire path only). Present ONLY on a
  // tie-START's payload, and only when the tie has >1 rendered head. The full
  // ordered set of the tie's heads (start first, then continuations), each with
  // its own stave-note graphic and its phrase-relative timestamp. The grading
  // fold (addEvents2) uses this to (a) merge every head's graphic onto the one
  // held NoteEvent so premature-release (yellow) / wrong-note (red) colour all
  // heads at once, and (b) carry each head's timestamp so the sustain path can
  // green heads progressively as the cursor reaches them. `timestamp` is
  // phrase-relative (same space as the slice currentTimestamp); addEvents2
  // rebases it onto the event's bucket timestamp via the start-head delta, so it
  // is offset-invariant. Undefined for untied notes and for the serialized
  // prerender (Path A), which highlights simultaneously and needs no per-head data.
  heads?: { graphic: PrerenderedGraphics; timestamp: number }[];
  // A tie across a line break (MUS-522): this note continues a tie that began in
  // the previous phrase. addEvents2 folds it into the held note that phrase ended
  // on, as it folds a continuation within a phrase. Live-OSMD / repertoire path only.
  continuesFromPreviousPhrase?: boolean;
};

export type PrerenderedVoiceEntry = {
  Notes: {
    fingering: Fingering | undefined;
    /** 0-100 need score of a finger number computed in the app; absent on numbers from the phrase file. */
    fingeringNeed?: number;
    graphics: PrerenderedGraphics[];
    isRest: boolean;
    pitch: Pitch;
    noteTie: SerializedNoteTie | undefined;
    length: number;

    printObject: boolean;
    staffPosition: string;
  }[];
};

export type PrerenderedPhraseData = {
  currentTimestamp: number;
  currentMeasureIndex: number;
  currentVoiceEntries: PrerenderedVoiceEntry[];
};

export type Composer = {
  name: string;
};

export type Repertoire = {
  id: number;
  name: string;
  composers: Composer[];
  repertoire_png_preview_url: string;
};

export type SetUnseenApptourFeatures = {
  type: typeof actionTypes.SET_UNSEEN_APPTOUR_FEATURES;
  payload: ApptourFeatureItem[];
};

export type MarkApptourFeaturesAsSeen = {
  type: typeof actionTypes.MARK_APPTOUR_FEATURES_AS_SEEN;
  payload: ApptourFeature[];
};

export type FetchUnseenApptourFeatures = {
  type: typeof actionTypes.FETCH_UNSEEN_APPTOUR_FEATURES;
};

export type CombinedActionTypes =
  | AppActions
  | LessonActions
  | RepertoireActions
  | EventActions
  | TutorialAction;
